<template>
  <div>
    <b-container fluid>
      <h1 v-if="title">
        {{title || model.title}}
        <b-button v-if="model.allowNew" variant="secondary" size="sm" :to="'/new/' + type">+ NEW</b-button>
        <b-button v-if="model.allowBulk" variant="secondary" size="sm" :to="'/bulk/' + type">Manage Bulk</b-button>
        <b-button v-if="type === 'companies' && (isSuperAdmin || isAccountant)" variant="primary" size="sm" @click.prevent="generateReport" :disabled="reportRequestInFlight">
          <translate>Generate Report</translate>
        </b-button>
      </h1>
      <b-row v-if="!hideFilters">
        <b-col cols="12">
          <div class="mb-3">
            <b-form-checkbox :button-variant="activeFilters > 0 ? 'success' : 'dark'" v-model="filtersOpened" size="sm" button>
              <font-awesome-icon icon="filter" />
              <span v-if="activeFilters > 0" v-translate="{activeFilters: activeFilters}" :translate-n="activeFilters" translate-plural="%{ activeFilters } active">%{activeFilters} active</span>
            </b-form-checkbox>
            <b-button size="sm" variant="danger" @click.prevent="$router.push({ query: null }); $root.$emit('bv::refresh::table', type); resetData()" v-if="activeFilters > 0">✕</b-button>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="!staticItems && (quickFilters || model.filters)">
        <b-col class="p-0">
          <list-filters ref="listFilters" :type="type" :model="model" :quick-filters="quickFilters" :no-querystring="noQuerystring" :filters-opened="filtersOpened" @apply-filters="refreshTable" @reset="resetData" />
        </b-col>
      </b-row>
      <b-row class="mb-3 align-items-center" v-if="allowSelect || listExtras">
        <b-col class="text-center" cols="12" lg="auto" order-lg="2" v-if="listExtras">
          <!-- List Extras Component is defined at the Model -->
          <component
            :is="listExtras"
            :for-csv="forCsv"
            :selected="selected"
            :has-results="hasResults"
            :has-updated-at-filter="hasUpdatedAtFilter"
            :has-created-at-filter="hasCreatedAtFilter"
            :current-filters="filters"
            :ref="type + 'ListExtras'"
          />
        </b-col>
        <b-col v-if="allowSelect" cols="12" lg="auto" order-lg="1">
          <b-form-checkbox v-if="allowSelect" inline v-model="selectAllTrigger" :disabled="!totalRows || isNaN(totalRows)"><translate>Select/unselect all</translate></b-form-checkbox>
          <!-- <b-form-checkbox v-if="hasColumnFilters" inline v-model="columnFilters"><translate>Show/Hide Column Filters</translate></b-form-checkbox> -->
        </b-col>
      </b-row>
    </b-container>
    <b-form-row class="overflow-x-auto">
      <b-table
        show-empty
        :small="compact"
        :empty-text="$gettext('Filter something to start 😊')"
        :empty-filtered-text="$gettext('There are no results 😞')"
        :tbody-tr-class="disabledClass"
        :id="type"
        ref="resultsTable"
        stacked="md"
        foot-clone
        :total-rows="totalRows"
        :sort-desc="sortDesc"
        :sort-by="sortBy"
        :per-page="perPage"
        :current-page="currentPage"
        :busy.sync="isBusy"
        :items="fetchItems"
        :fields="tableFieldsStatic || tableFields"
        :api-url="this.model.endpoint ? this.model.endpoint : null"
        @sort-changed="onFiltered"
        select-mode="multi"
        selectedVariant="secondary"
        :selectable="allowSelect"
        @row-selected="rowSelected"
        :filter="filters && filters.filtersToEmit && filters.filtersToEmit.length > 0 ? 'true' : ''"
        @head-clicked="thClicked"
      >

        <template #head(actions)="">
          <font-awesome-icon icon="table" :id="'tooltip-table-fields-customizer-' + type" />
        </template>

        <template #foot(actions)=""></template>

        <template #head()="data">
          <span v-if="columnFilters && data.field.columnFilter" class="text-nowrap">
            <span v-html="data.field.label"></span> <column-filters :type="type" :filter="data.field" :no-querystring="noQuerystring" @apply-filters="refreshTable" />
            <!-- **** LOOK FOR FILTER IN MODEL **** -->
          </span>
        </template>
        <template #foot()="data">
          <span v-html="data.field.label"></span>
        </template>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong v-translate>Loading...</strong>
          </div>
        </template>

        <template #cell()="data">
          <component v-if="tableFields.findIndex(x => x.key === data.field.key) !== -1 && tableFields.find(x => x.key === data.field.key).component" :is="tableFields.find(x => x.key === data.field.key).component(data.value, data.index, data.item).is" v-b-tooltip.html v-bind="tableFields.find(x => x.key === data.field.key).component(data.value, data.field.key, data.item)" @updated="refreshTable"></component>
          <div v-else-if="tableFields.findIndex(x => x.key === data.field.key) !== -1 && tableFields.find(x => x.key === data.field.key).filter" v-html="$options.filters[tableFields.find(x => x.key === data.field.key).filter](data.value)"></div>
          <div v-else-if="tableFields.findIndex(x => x.key === data.field.key) !== -1 && tableFields.find(x => x.key === data.field.key).formatter" v-html="data.value"></div>
          <div v-else>{{data.value}}</div>
        </template>

        <template #cell(id)="data">
          <!-- Defined in Model -->
          <component v-if="tableFields.findIndex(x => x.key === data.field.key) !== -1 && tableFields.find(x => x.key === data.field.key).component" :is="tableFields.find(x => x.key === data.field.key).component(data.value, data.index, data.item).is" v-b-tooltip.html v-bind="tableFields.find(x => x.key === data.field.key).component(data.value, data.field.key, data.item)" @updated="refreshTable"></component>
          <!-- Default/s -->
          <div v-else-if="type && !model.noIdLink && $router.options.routes && $router.options.routes.findIndex(x => x.name === type.charAt(0).toUpperCase() + type.slice(1) + 'Edit') !== -1" class="text-nowrap">
            <b-link :to="{name: type.charAt(0).toUpperCase() + type.slice(1) + 'Edit', params: {id: data.value}}">{{data.value | uuid }}</b-link>
            <a href="#" @click.prevent="copyToClipboard(data.value)" class="text-secondary" :title="$gettext('Copy to Clipboard')">
              <font-awesome-icon :icon="['far', 'copy']" />
            </a>
          </div>
          <div v-else>
            {{data.value | cleanuphtml}}
          </div>
        </template>

        <template #cell(roles)="row">
          <div v-if="$options.filters.allowedroles(row.value).length > 0">
            <span v-for="(item, index) in $options.filters.allowedroles(row.value)" :key="index">
              <b-badge variant="dark" v-translate>{{ item.name }}</b-badge>&nbsp;
            </span>
          </div>
          <div v-if="row.item.tags.length > 0 || (row.item.company && row.item.company.tags.length > 0)" :class="{'mt-3': $options.filters.allowedroles(row.value).length > 0}">
            <span v-for="(item, index) in row.item.tags" :key="'user-tags-' + index">
              <b-badge v-b-tooltip.html variant="danger" :title="$gettext('User tag')" v-translate>{{ item.name }}</b-badge>&nbsp;
            </span>
            <span v-if="row.item.company && row.item.company.tags.length > 0">
              <span v-for="(item, index) in row.item.company.tags" :key="'company-tags-' + index">
                <b-badge v-b-tooltip.html variant="info" :title="$gettext('Company tag')" v-translate>{{ item.name }}</b-badge>&nbsp;
              </span>
            </span>
          </div>
        </template>

        <template v-slot:cell(tags)="row">
          <div v-if="row.item.tags.length > 0" style="width: 220px;">
            <span v-for="(item, index) in row.item.tags" :key="index">
              <b-badge pill variant="danger">{{ item.name }}</b-badge>&nbsp;
            </span>
          </div>
        </template>

        <template v-slot:cell(phone)="row">
          {{row.item.phone | phone(row.item.country_iso_code || 'ar')}}
        </template>

        <template #cell(active)="row">
          <span v-b-tooltip.html :title="row.item.active ? $gettext('Enabled') : $gettext('Disabled')" style="display: inline-block; width:10px; height:10px; border-radius: 100px;" :class="row.item.active ? 'bg-success' : 'bg-danger'"></span>
          <!-- A model can add extra markup next to the dot (e.g. a status badge) with a formatter -->
          <span v-if="tableFields.find(x => x.key === 'active')?.formatter" v-html="row.value"></span>
        </template>

        <template #cell(actions)="row">
          <b-dropdown v-if="hasRowActions(row.item)" dropright boundary="viewport" size="sm" variant="link" toggle-class="text-decoration-none" no-caret>
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="text-dark" size="2x" style="font-size: 12px; vertical-align: middle" />
            </template>

            <component
              :is="action.type"
              v-if="model.extraActions && (!action.position || action.position !== 'end') && isActionVisible(action, row.item)"
              v-for="(action, index) in model.extraActions"
              :key="index"
              :size="action.size || 'sm'"
              :variant="action.variant || 'primary'"
              @click.stop="(action.action ? action.action(row.item) : void(0))"
              :to="action.to ? action.to(row.item) : void(0)"
              :title="action.title || ''"
              :disabled="(action.disabledCondition && action.disabledCondition(row.item))"
              v-bind="action.props ? action.props(row.item) : null"
            >
              <font-awesome-icon v-if="action.icon" :icon="action.icon" />
              {{action.label || null}}
            </component>

            <!-- <b-dropdown-item-button
              v-if="ecosystemModules && ecosystemModules[type] && ecosystemModules[type].geolocalizableBy"
              variant="dark"
              @click.prevent="selectedRow = row.item"
              v-b-modal.modalRowMap
            >
              <font-awesome-icon icon="map-marker-alt" />
              <translate>View in map</translate>
            </b-dropdown-item-button> -->

            <b-dropdown-item-button
              v-if="editInPlace"
              variant="primary"
              @click.stop="editingRow = JSON.parse(JSON.stringify(row))"
              v-b-modal="'modalEditInPlace' + type"
            >
              <font-awesome-icon icon="edit" />
              <translate>Edit</translate>
            </b-dropdown-item-button>

            <b-dropdown-item-button
              v-if="allowDelete && isSuperAdmin"
              variant="danger"
              @click.stop="deleteRow(row.item.id)"
            >
              <font-awesome-icon icon="trash-alt" />
              <translate>Delete</translate>
            </b-dropdown-item-button>

            <component
              :is="action.type"
              v-if="model.extraActions && action.position === 'end' && isActionVisible(action, row.item)"
              v-for="(action, index) in model.extraActions"
              :key="index"
              :size="action.size || 'sm'"
              :variant="action.variant || 'primary'"
              @click.stop="(action.action ? action.action(row.item) : void(0))"
              :to="action.to ? action.to(row.item) : void(0)"
              :target="action.target || null"
              :title="action.title || ''"
              :disabled="(action.disabledCondition && action.disabledCondition(row.item))"
              v-bind="action.props ? action.props(row.item) : null"
            >
              <font-awesome-icon v-if="action.icon" :icon="action.icon" />
              {{action.label || null}}
            </component>
          </b-dropdown>
        </template>

      </b-table>

      <div id="table-commands" :class="inModal ? 'in-modal' : ''" >
        <span class="d-inline-block mr-3" v-if="!hidePagination && totalRows > perPage">
          <b-pagination v-if="totalRows > perPage" :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0" first-number
            last-number prev-text="◀" next-text="▶" />
        </span>
        <span class="d-inline-block mr-3">
          <div v-translate="{totalRows: totalRows}">
            Total items: <strong>%{totalRows}</strong>
          </div>
        </span>
        <span class="d-inline-block mr-3" v-if="!hidePagination">
          <b-form-group horizontal :label="$gettext('Per page')" label-cols="6" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" style="font-size: 1em" />
          </b-form-group>
        </span>
        <span class="d-inline-block">
          <b-form-checkbox switch v-model="compact" checked="true" unchecked="false"><translate>Compact</translate></b-form-checkbox>
        </span>
        <ApiListMultiSort :model="model" @updated="applyMultiSort" :previous-sorts="multiSort" :disabled="!hasResults"></ApiListMultiSort>
      </div>
    </b-form-row>

    <b-modal :id="'modalEditInPlace' + type" :ref="'modalEditInPlace' + type" size="lg" hide-footer>
      <template #modal-title>Edit {{model.title}}</template>
      <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
      <api-item-edit :model="model" :id="!model.localEdit ? editingRow.item.id : null" :staticItem="model.localEdit ? editingRow : null" :type="this.type" v-if="editingRow.item" @success="updateInPlace" />
    </b-modal>

    <b-tooltip :target="'tooltip-table-fields-customizer-' + type" triggers="hover">
      <api-list-fields-customization :type="type" :model="model" @refresh-fields="remapTableFields" />
    </b-tooltip>
  </div>

</template>

<script>
import Draggable from 'vuedraggable'
import ListFilters from './ListFilters/ListFilters'
import ColumnFilters from './ListFilters/ColumnFilters'
import ApiItemEdit from './ApiItemEdit'
import ApiListFieldsCustomization from './ApiListFieldsCustomization'
import ListMixin from '../../mixins/list.mixin'
import ApiListMultiSort from './ApiListMultiSort.vue'

export default {
  name: 'ApiList',
  components: {
    Draggable,
    ListFilters,
    ColumnFilters,
    ApiItemEdit,
    ApiListFieldsCustomization,
    ApiListMultiSort
  },
  props: {
    /**
     * Keys the per-list preferences persisted in localStorage (tableFields, perPage,
     * compact). A list mounted without a type gets no persisted customization.
     *
     * NOT reactive: the component does not watch this prop, so changing it on a mounted
     * list leaves the previous type's data, filters and preferences in place. To switch
     * types, remount the list with `:key="type"`. That re-runs data() (re-reading perPage
     * and compact for the new type) and mounted() (including allowDelete), mounts fresh
     * filter children, and keeps the usual firstRun semantics.
     */
    type: {
      type: String,
      required: false
    },
    title: {
      type: [String, Boolean],
      default: null
    },
    model: {
      type: Object,
      required: true
    },
    quickFilters: {
      type: Object,
      default: () => {}
    },
    preFilters: {
      type: Array,
      default: () => {
        return []
      }
    },
    prePerPage: {
      type: Number,
      default: 25
    },
    staticItems: {
      type: Array,
      default: null
    },
    preFields: {
      type: Array,
      default: () => {
        return []
      }
    },
    filterOnMount: {
      type: Boolean,
      default: false
    },
    noQuerystring: {
      type: Boolean,
      default: false
    },
    inModal: {
      type: Boolean,
      default: false
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    hidePagination: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      exclude: [],
      // tableFieldsStatic: null,
      // includes: [],
      allowDelete: null,
      isBusy: false,
      firstRun: true,
      currentPage: 1,
      perPage: this.getLocalStorage(this.preferenceStorageKey('perPage')) && this.getLocalStorage(this.preferenceStorageKey('perPage'))[this.type] ? this.getLocalStorage(this.preferenceStorageKey('perPage'))[this.type] : this.prePerPage,
      totalRows: '-',
      pageOptions: [10, 25, 50, 100, 500],
      sortBy: 'updated_at',
      sortDesc: true,
      sortDirection: 'desc',
      filters: {},
      dismissSecs: 5,
      dismissCountDown: 0,
      deleted: false,
      editingRow: {},
      selectedRow: {},
      newB2bShipping: {},
      forCsv: {},
      selectAllTrigger: false,
      selected: [],
      compact: this.getLocalStorage(this.preferenceStorageKey('compact')) && this.getLocalStorage(this.preferenceStorageKey('compact'))[this.type] ? this.getLocalStorage(this.preferenceStorageKey('compact'))[this.type] : false,
      filtersOpened: false,
      activeFilters: 0,
      columnFilters: false,
      multiSort: []
    }
  },
  computed: {
    applyFilter() {
      this.currentPage = 1
      return this.filter
    },
    editInPlace() {
      return this.model.editInPlace || false
    },
    hasCreatedAtFilter() {
      return JSON.stringify(this.filters).indexOf('created_at') !== -1
    },
    hasUpdatedAtFilter() {
      return JSON.stringify(this.filters).indexOf('updated_at') !== -1
    },
    hasResults() {
      return this.totalRows !== '-' ? !!this.totalRows : false
    },
    listExtras() {
      return this.model.listExtrasComponent
    },
    allowSelect() {
      return this.model.allowSelect
    },
    includes() {
      return this.model.includes
    },
    hasColumnFilters() {
      const fields = this.tableFieldsStatic || this.tableFields
      return fields.filter(x => x.columnFilter).length > 0
    }
  },
  watch: {
    exclude() {
      return this.model.exclude
    },
    selectAllTrigger(val) {
      if (val === true) {
        this.selectAll()
      } else {
        this.unselectAll()
      }
    },
    multiSort: {
      handler() {
        const orderHtml = document.querySelectorAll('.multi-sort-order')
        for (const element of orderHtml) {
          element.remove()
        }
        const ths = document.body.querySelectorAll('th[aria-sort]')
        for (const th of ths) {
          th.setAttribute('aria-sort', 'none')
        }
        if (this.multiSort.length) {
          setTimeout(() => {
            let order = 1
            for (const sort of this.multiSort) {
              const element = document.querySelectorAll(`.col-${sort.sortBy}`)[0]
              element.setAttribute('aria-sort', sort.sortDirection === 'asc' ? 'ascending' : 'descending')
              const orderHtml = document.querySelectorAll(`.col-${sort.sortBy} > .multi-sort-order`)[0]
              if (orderHtml?.innerHTML) {
                orderHtml.innerHTML = order++
              } else {
                element.innerHTML = element.innerHTML + '<span class="multi-sort-order text-muted">' + order++ + '</span>'
              }
            }
          }, 100)
        }

        this.refreshTable()
      },
      deep: true
    }
  },
  mixins: [ListMixin],
  mounted() {
    this.$root.$on('activeFilters', ($e) => {
      this.activeFilters = $e
    })

    if (this.$route.query.created === true) {
      this.showAlert()
    }
    if (this.$route.query.deleted === true) {
      this.deleted = true
      this.showAlert()
    }
    this.exclude = this.model.exclude
    this.allowDelete = this.model.allowDelete

    if (this.filterOnMount) {
      setTimeout(() => {
        this.refreshTable()
      }, 500)
    }
  },
  methods: {
    applyMultiSort(sorts) {
      this.multiSort = sorts
    },
    isActionVisible(action, item) {
      return !action.hideCondition || !action.hideCondition(item)
    },
    // A divider alone is not an action: rows without any action get no dropdown
    hasRowActions(item) {
      return this.editInPlace ||
        (this.allowDelete && this.isSuperAdmin) ||
        (this.model.extraActions || []).some(action => action.type !== 'b-dropdown-divider' && this.isActionVisible(action, item))
    },
    thClicked(key, field, event) {
      if (event.altKey) {
        event.preventDefault()
        event.stopImmediatePropagation()
        event.stopPropagation()
        this.discernSort(key)
      } else {
        this.multiSort = []
      }
    },
    discernSort(key) {
      const sortIndex = this.multiSort.findIndex(x => x.sortBy === key)
      if (sortIndex === -1) {
        this.multiSort.push({
          sortBy: key,
          sortDirection: 'asc'
        })
      } else {
        if (this.multiSort[sortIndex].sortDirection === 'asc') {
          this.multiSort[sortIndex].sortDirection = 'desc'
        } else {
          this.multiSort[sortIndex].sortDirection = 'asc'
        }
      }
    },
    generateReport() {
      const params = {
        format: 'upload',
        filter_groups: []
      }
      if (!this.$cookies.get('bypassCountry') && this.model.countrize) {
        params.filter_groups.push(
          {
            filters: [
              [this.model.countrize, 'eq', window.country]
            ]
          }
        )
      }
      this.generateReportAsync({
        url: this.$appConfig.api.baseUrl + this.type,
        params,
        message: this.$gettext('The Companies Report is being generated, you will receive an email with a link to download it as soon as it\'s done')
      })
    },
    async fetchItems(ctx) {
      if (this.$route.query.af || this.$route.query.qf) {
        this.filtersOpened = true
      }
      if (!this.staticItems) {
        if (!this.model.fetchItems) {
          this.$store?.dispatch('hideSpinner')
          if (!this.firstRun) {
            this.selectAllTrigger = false
            console.log(ctx)
            const params = {
              page: ctx.currentPage - 1,
              limit: ctx.perPage,
              includes: this.includes,
              sort: [
                {
                  key: ctx.sortBy || 'id',
                  direction: ctx.sortDesc ? 'desc' : 'asc'
                }
              ]
            }
            if (this.multiSort.length) {
              params.sort = this.multiSort.map(x => {
                return {
                  key: x.sortBy,
                  direction: x.sortDirection
                }
              })
            }
            if (this.model.specificFields) {
              params.fields = this.model.specificFields
            }
            if (this.preFilters || this.filters.filtersToEmit) {
              let filters = this.preFilters
              if (this.filters) {
                filters = filters.concat(this.filters.filtersToEmit)
              }
              params.filter_groups = [
                {
                  filters
                }
              ]
            }

            if (this.filters.plainFiltersToEmit && this.filters.plainFiltersToEmit.length > 0) {
              for (let i = 0; i < this.filters.plainFiltersToEmit.length; i++) {
                const filterGroup = this.filters.plainFiltersToEmit[i]
                params.filter_groups.push(filterGroup)
              }
            }

            if (!this.$cookies.get('bypassCountry') && this.model.countrize) {
              params.filter_groups.push(
                {
                  filters: [
                    [this.model.countrize, 'eq', window.country]
                  ]
                }
              )
            }

            this.forCsv = {
              url: ctx.apiUrl,
              params: {
                format: 'upload',
                filter_groups: params.filter_groups
              }
            }

            console.log('###############################################################################', this.dataTransformers)

            return this.$http.get(ctx.apiUrl, { params })
              .then(response => {
                // Data Transformer for entity must be defined at @/common/dataTransformersDefinitions/* on parent project
                if (this.dataTransformers && this.dataTransformers[this.type]) {
                  if (this.dataTransformers[this.type].transform) {
                    response.body.data = response.body.data.map(x => {
                      return this.dataTransformers[this.type].transform(x)
                    })
                  }
                  if (this.dataTransformers[this.type].transformAll) {
                    response.body.data = this.dataTransformers[this.type].transformAll(response.body.data)
                  }
                }

                const items = response.body.data
                this.totalRows = response.body.pagination.total
                this.$emit('itemsLoaded', items)
                this.$emit('totalRows', this.totalRows)
                return (items)
              }).catch(error => {
                console.log(error)
                return []
              })
          } else {
            this.firstRun = false
            return []
          }
        } else {
          const externalFetch = await this.model.fetchItems(ctx)
          if (
            typeof externalFetch === 'object' &&
            !Array.isArray(externalFetch) &&
            externalFetch !== null
          ) {
            this.totalRows = externalFetch.totalRows
            this.$emit('itemsLoaded', externalFetch.items)
            this.$emit('totalRows', this.totalRows)
            return externalFetch.items
          } else {
            return externalFetch
          }
        }
      } else {
        this.totalRows = this.staticItems.length
        this.$emit('itemsLoaded', this.staticItems)
        this.$emit('totalRows', this.totalRows)
        return this.staticItems
      }
    },
    refreshTable(filters) {
      console.log(filters)
      this.currentPage = 1
      this.filters = filters || this.filters

      this.$root.$emit('bv::refresh::table', this.type)
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length || '-'
      this.currentPage = 1
    },
    deleteRow(id) {
      if (
        confirm('Are you sure you want to DELETE the row with ID:\n' + id + '\nWARNING: THIS ACTION CANNOT BE UNDONE!')
      ) {
        const endpoint = this.model.endpointDelete ? this.model.endpointDelete.replace('{id}', id) : this.model.endpoint + '/' + id
        this.$http.delete(endpoint)
          .then(() => {
            this.refreshTable(this.filters)
          })
      }
    },
    updateInPlace(data) {
      if (!this.staticItems) {
        this.refreshTable(this.filters)
      } else {
        this.staticItems[data.index] = data.item
        this.editingRow = {}
        this.refreshTable(this.filters)
      }
      if (this.model.editInPlace) {
        this.$refs['modalEditInPlace' + this.type].hide()
      }
    },
    resetData() {
      Object.assign(this.$data, this.$options.data.call(this))
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
      // },
      // initMap(item) {
      //   let infoWindowHtml = ''
      //   infoWindowHtml += item.reference_code
      //   let itemColor
      //   if (this.ecosystemModules[this.type].icon.colorByMinAge) {
      //     const daysIdle = this.elapsedTimeRaw(item.last_position_updated_at) / 86400000
      //     itemColor = this.itemColorByAge(daysIdle, 'items')
      //   }

      //   let direction = 0
      //   if (this.ecosystemModules.items.directionableBy) {
      //     try {
      //       direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
      //     } catch (error) {
      //       console.log(error)
      //     }
      //   }

      //   let dynamic = null
      //   try {
      //     if (this.ecosystemModules.items.dynamicableBy) {
      //       dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item))
      //     }
      //   } catch (e) {
      //     console.log(e)
      //   }

      //   console.log(item)
      //   let marker = {
      //     id: 100000,
      //     cardType: 'item',
      //     lat: item[this.ecosystemModules[this.type].geolocalizableBy].lat || item.lat,
      //     lng: item[this.ecosystemModules[this.type].geolocalizableBy].lng || item.lng,
      //     icon: {
      //       type: this.ecosystemModules[this.type].icon.type,
      //       size: this.ecosystemModules[this.type].icon.size,
      //       anchor: this.ecosystemModules[this.type].icon.anchor,
      //       color: item.active ? (itemColor || '#000') : '#333',
      //       text: this.ecosystemModules[this.type].icon.text,
      //       direction: direction,
      //       dynamic: dynamic
      //     },
      //     x: 30,
      //     y: 30,
      //     optimized: false,
      //     infoText: {
      //       id: item.id,
      //       type: 'item',
      //       html: infoWindowHtml,
      //       color: item.active ? itemColor : '#333',
      //       data: item,
      //       isShared: item.isShared || false
      //     },
      //     popup: true
      //   }

    //   this.$refs.rowMap.addMarkers([marker])
    //   if (this.fitted === false) {
    //     this.$refs.rowMap.fitted = false
    //     this.fitted = true
    //   }
    // },
    // createShippingFromItem(data) {
    //   console.log(data)
    //   this.newB2bShipping = {
    //     type: 'regular',
    //     flow: 'semi-automatic',
    //     size: 'palet',
    //     from: {
    //       coords: {
    //         lat: data.item_tracking.lat,
    //         lng: data.item_tracking.lng
    //       },
    //       addressDescription: data.item_tracking.meta.address,
    //       contact: {
    //         firstName: this.profile.company.contact_name,
    //         lastName: '',
    //         email: this.profile.company.email,
    //         phone: this.profile.company.phone
    //       },
    //       message: ''
    //     },
    //     to: {
    //       placeId: null,
    //       coords: {
    //         lat: null,
    //         lng: null
    //       },
    //       addressDescription: '',
    //       contact: {
    //         firstName: '',
    //         lastName: '',
    //         email: '',
    //         phone: ''
    //       },
    //       message: ''
    //     },
    //     internalCode: data.reference_code,
    //     description: '',
    //     conf: {
    //       assurance: false
    //     },
    //     items: [
    //       {...data, ...{item_id: data.id}}
    //     ]
    //   }
    //   console.log(this.newB2bShipping)
    //   this.$refs.modalShippingsCreateFromItem.show()
    // },
    // shippingCreated() {
    //   this.$refs.modalShippingsCreateFromItem.hide()
    },
    disabledClass(item, type) {
      if (!item || type !== 'row') return
      if (item.active === 0) return 'text-muted'
    },
    rowSelected(items) {
      this.selected = items
    },
    selectAll() {
      const resultsTable = this.$refs.resultsTable.$el
      const tableBody = resultsTable.getElementsByTagName('tbody')[0]
      const tableRows = tableBody.getElementsByTagName('tr')
      this.unselectAll()
      for (const row of tableRows) {
        row.click()
      }
    },
    unselectAll() {
      this.$refs.resultsTable.clearSelected()
    }
  }
}
</script>

<style lang="scss">
#table-commands {
  position: fixed;
  bottom: 0;
  right: 15px;
  background-color: #fff;
  border-radius: 5px 5px 0 0;
  box-shadow: 0 0 10px rgba($color: #000000, $alpha: 0.2);
  padding: 10px 15px;
  transition: box-shadow 0.15s ease-in-out;
  z-index: 1;
  &:hover {
    box-shadow: 0 0 4px rgba($color: #000000, $alpha: 0.35);
  }
  &.in-modal {
    position: absolute;
    bottom: -20px;
  }
}
.overflow-x-auto {
  overflow-x: auto;
}
.multi-sort-order {
  position: absolute;
  right: calc(0.75rem + 0.25em); /* 0.65em); */
  top: 50%;
  transform: translateY(-50%);
}
</style>
